"use client";

import {
  Stack,
  Box,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  FormLabel,
  Button,
  Spinner,
  Input,
  Select,
} from "@chakra-ui/react";
import "services/Recruit/assets/css/CreateJobOpening/style.css";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { globalStyles } from "common/theme/styles";
import { Field, Formik, Form } from "formik";
import { useEffect, useState, useMemo, useCallback } from "react";
import TextEditor from "common/components/textEditor/TextEditor";
import { useNavigate, useParams } from "react-router-dom";
import { constant } from "common/constant";
import ActionNavbar from "common/components/actionBar/ActionNavbar";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getApi } from "common/services/api";
import { fetchUserData } from "common/redux/reducers/userSlice";
import ReactSelect from "react-select";
import * as Yup from "yup";
import { HasAccess } from "common/utils/accessUtils";
import { accessModuleTitles } from "common/constant";
import { feedbackConstants } from "common/constant";
import { API_PATHS } from "common/services/apiPaths";
import { postApi } from "common/services/api";
import { feedbackTabsData } from "services/Recruit/data/tabsData";
import { recruitConstant } from "services/Recruit/constant";
import { jobTitleOptions } from "services/Recruit/data/dropdownData";
import { useLocation } from "react-router-dom";
import { ratingOptions, experienceRangeOptions } from "services/Recruit/data/dropdownData";
import Rating from "services/Recruit/components/Rating/Rating";

const CreateFeedback = () => {
  const colors = globalStyles.colors;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [highestTabReached, setHighestTabReached] = useState(0);
  const { id } = useParams();
  const [feedbackData, setFeedbackData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [interviewData, setInterviewData] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState({});
  const [permission] = HasAccess([accessModuleTitles.Feedback]);
  const [candidateData, setCandidateData] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState({});
  const { users } = useSelector((state) => state.users);
  const location = useLocation();
  const viewMode = location.state?.viewMode ?? false;

  // Allow form editing if: user has create permission OR navigating from interview (has id)
  const canEdit = !viewMode && (permission?.create || id);

  // Fetch users for interview panel members
  useEffect(() => {
    dispatch(fetchUserData());
  }, [dispatch]);

  const defaultQuestions = [
    {
      question: feedbackConstants.technicalInterviewQuestions,
      answer: "",
      required: true,
    },

    {
      question: feedbackConstants.practicalSkillsAssessmentQuestions,
      answer: "",
      required: true,
    },
    {
      question: feedbackConstants.additionalQuestions,
      answer: "",
      required: false,
    },
    {
      question: feedbackConstants.finalComments,
      answer: "",
      required: true,
    },
  ];

  const recommendedActions = [
    { value: "proceed", label: "Proceed to Next Stage" },
    { value: "consider", label: "Needs Further Discussion" },
    { value: "rejected", label: "Not Suitable" },
  ];

  // Define validation schemas for each tab
  const tabValidationSchemas = useMemo(
    () => ({
      0: Yup.object().shape({
        interview: Yup.string().required("Interview is required"),
        interviewPanelMembers: Yup.array()
          .of(Yup.string())
          .min(1, "At least one panel member is required")
          .required("Interview panel members are required"),
        round: Yup.string().required("Interview round is required"),
        candidate: Yup.string().required("Candidate is required"),
        postingTitle: Yup.string().required("Profile/Position is required"),
        technicalKnowledge: Yup.number().when("round", {
          is: (round) => round !== "HR Round",
          then: (schema) => schema
            .required("Technical knowledge rating is required")
            .min(1, "Rating must be at least 1")
            .max(5, "Rating can be at most 5"),
          otherwise: (schema) => schema.nullable(),
        }),
        technicalKnowledgeDetails: Yup.string().when("round", {
          is: (round) => round !== "HR Round",
          then: (schema) => schema.required("Technical knowledge details are required"),
          otherwise: (schema) => schema.nullable(),
        }),
        communication: Yup.number().when("round", {
          is: (round) => round !== "HR Round",
          then: (schema) => schema
            .required("Communication rating is required")
            .min(3, "Rating must be at least 3")
            .max(5, "Rating can be at most 5"),
          otherwise: (schema) => schema.nullable(),
        }),
        problemSolvingSkills: Yup.number().when("round", {
          is: (round) => round !== "HR Round",
          then: (schema) => schema
            .required("Problem-solving skills rating is required")
            .min(1, "Rating must be at least 1")
            .max(5, "Rating can be at most 5"),
          otherwise: (schema) => schema.nullable(),
        }),
        problemSolvingDetails: Yup.string().when("round", {
          is: (round) => round !== "HR Round",
          then: (schema) => schema.required("Problem-solving details are required"),
          otherwise: (schema) => schema.nullable(),
        }),
        rating: Yup.number()
          .required("Final rating is required")
          .min(1, "Rating must be at least 1")
          .max(5, "Rating can be at most 5"),
        experience: Yup.string().required("Experience is required"),
        recommendedAction: Yup.string().required(
          "Recommended action is required"
        ),
        questionResponses: Yup.array().of(
          Yup.object().shape({
            question: Yup.string().required("Question is required"),
            answer: Yup.string().when("required", {
              is: true,
              then: (schema) => schema.required("This question is required"),
              otherwise: (schema) => schema.nullable(),
            }),
            required: Yup.boolean().default(false),
          })
        ),
      }),
    }),
    []
  );

  // Function to check if all mandatory fields are filled
  const areMandatoryFieldsFilled = useCallback((values) => {
    const isHRRound = values.round === "HR Round";

    // Check interview
    if (!values.interview || values.interview.trim() === "") {
      return false;
    }

    // Check interviewPanelMembers - must have at least one
    if (
      !values.interviewPanelMembers ||
      values.interviewPanelMembers.length === 0
    ) {
      return false;
    }

    // Check round
    if (!values.round || values.round.trim() === "") {
      return false;
    }

    // Check candidate
    if (!values.candidate || values.candidate.trim() === "") {
      return false;
    }

    // Check postingTitle (Profile/Position)
    if (!values.postingTitle || values.postingTitle.trim() === "") {
      return false;
    }

    // For HR Round, only check final rating, experience, and recommended action
    if (isHRRound) {
      // Check final rating
      const rating = values.rating;
      if (
        rating === null ||
        rating === undefined ||
        rating === "" ||
        isNaN(Number(rating)) ||
        rating < 1 ||
        rating > 5
      ) {
        return false;
      }

      // Check experience
      if (!values.experience || values.experience.trim() === "") {
        return false;
      }

      // Check recommendedAction
      if (
        !values.recommendedAction ||
        (typeof values.recommendedAction === "string" &&
          values.recommendedAction.trim() === "")
      ) {
        return false;
      }

      return true; // All HR Round mandatory fields are filled
    }

    // For non-HR rounds, check all technical fields
    // Check technicalKnowledge - must be a valid number (1-5)
    const technicalKnowledge = values.technicalKnowledge;
    if (
      technicalKnowledge === null ||
      technicalKnowledge === undefined ||
      technicalKnowledge === "" ||
      isNaN(Number(technicalKnowledge)) ||
      technicalKnowledge < 1 ||
      technicalKnowledge > 5
    ) {
      return false;
    }

    // Check technicalKnowledgeDetails
    const techDetails = values.technicalKnowledgeDetails || "";
    const cleanedTechDetails = techDetails.replace(/<[^>]*>/g, "").trim();
    if (cleanedTechDetails === "" || cleanedTechDetails === "<p><br></p>") {
      return false;
    }

    // Check communication - must be a valid number (3-5)
    const communication = values.communication;
    if (
      communication === null ||
      communication === undefined ||
      communication === "" ||
      isNaN(Number(communication)) ||
      communication < 3 ||
      communication > 5
    ) {
      return false;
    }

    // Check problemSolvingSkills - must be a valid number (1-5)
    const problemSolvingSkills = values.problemSolvingSkills;
    if (
      problemSolvingSkills === null ||
      problemSolvingSkills === undefined ||
      problemSolvingSkills === "" ||
      isNaN(Number(problemSolvingSkills)) ||
      problemSolvingSkills < 1 ||
      problemSolvingSkills > 5
    ) {
      return false;
    }

    // Check problemSolvingDetails
    const problemDetails = values.problemSolvingDetails || "";
    const cleanedProblemDetails = problemDetails.replace(/<[^>]*>/g, "").trim();
    if (cleanedProblemDetails === "" || cleanedProblemDetails === "<p><br></p>") {
      return false;
    }

    // Check final rating
    const rating = values.rating;
    if (
      rating === null ||
      rating === undefined ||
      rating === "" ||
      isNaN(Number(rating)) ||
      rating < 1 ||
      rating > 5
    ) {
      return false;
    }

    // Check experience
    if (!values.experience || values.experience.trim() === "") {
      return false;
    }

    // Check recommendedAction
    if (
      !values.recommendedAction ||
      (typeof values.recommendedAction === "string" &&
        values.recommendedAction.trim() === "")
    ) {
      return false;
    }

    // Check required question responses
    if (values.questionResponses && Array.isArray(values.questionResponses)) {
      for (const questionResponse of values.questionResponses) {
        if (questionResponse.required) {
          // Check if answer is empty or just whitespace/HTML tags
          const answer = questionResponse.answer || "";
          const cleanedAnswer = answer.replace(/<[^>]*>/g, "").trim(); // Remove HTML tags and trim
          if (cleanedAnswer === "" || cleanedAnswer === "<p><br></p>") {
            return false;
          }
        }
      }
    }

    return true;
  }, []);

  // Function to check if a specific tab is valid
  const isTabValid = useCallback(
    (values, tabIndex) => {
      try {
        tabValidationSchemas[tabIndex].validateSync(values, {
          abortEarly: false,
        });
        return true;
      } catch (error) {
        return false;
      }
    },
    [tabValidationSchemas]
  );

  // Function to get tab navigation states
  const getTabStates = useCallback(
    (values, dirty, isValid, currentTabIndex) => {
      const tabStates = {};

      // Find the first invalid tab among visited tabs
      let firstInvalidTab = null;
      for (let i = 0; i <= highestTabReached; i++) {
        if (!isTabValid(values, i)) {
          firstInvalidTab = i;
          break;
        }
      }

      for (let i = 0; i <= 1; i++) {
        if (i < currentTabIndex) {
          // Previous tabs are always accessible
          tabStates[i] = { isAccessible: true, isValid: isTabValid(values, i) };
        } else if (i === currentTabIndex) {
          // Current tab - always accessible
          const currentTabValid = isTabValid(values, i);
          tabStates[i] = {
            isAccessible: true,
            isValid: currentTabValid,
            canProceed: currentTabValid,
          };
        } else if (
          i <= highestTabReached &&
          (firstInvalidTab === null || i <= firstInvalidTab)
        ) {
          // Previously visited tabs - accessible only if no invalid tabs before them
          tabStates[i] = {
            isAccessible: true,
            isValid: isTabValid(values, i),
            canProceed: false,
          };
        } else {
          // Future tabs or tabs after first invalid tab - not accessible
          tabStates[i] = {
            isAccessible: false,
            isValid: false,
            canProceed: false,
          };
        }
      }

      return tabStates;
    },
    [isTabValid, highestTabReached]
  );

  const handleTabChange = useCallback(
    (index, values, dirty, isValid) => {
      // Allow going back to previous tabs without validation
      if (index < activeTabIndex) {
        setActiveTabIndex(index);
        return;
      }

      // If trying to stay on the same tab, allow it
      if (index === activeTabIndex) {
        return;
      }

      // Check if navigation is allowed based on tab states
      const tabStates = getTabStates(values, dirty, isValid, activeTabIndex);

      if (tabStates[index]?.isAccessible) {
        setActiveTabIndex(index);
      } else {
        toast.error("Please complete the current step before proceeding");
      }
    },
    [activeTabIndex, getTabStates]
  );

  const handleNext = useCallback(
    (values, dirty, isValid, setErrors, setTouched) => {
      if (activeTabIndex < 1) {
        const currentTabValid = isTabValid(values, activeTabIndex);

        if (currentTabValid) {
          const newTabIndex = activeTabIndex + 1;
          setActiveTabIndex(newTabIndex);
          setHighestTabReached(Math.max(highestTabReached, newTabIndex));
        } else {
          // Validate current tab to show errors
          try {
            tabValidationSchemas[activeTabIndex].validateSync(values, {
              abortEarly: false,
            });
          } catch (error) {
            const errors = {};
            const touchedFields = {};
            error.inner.forEach((err) => {
              errors[err.path] = err.message;
              touchedFields[err.path] = true;
            });
            setErrors(errors);
            setTouched(touchedFields);
          }
          toast.error("Please fill all required fields before proceeding");
        }
      }
    },
    [activeTabIndex, isTabValid, tabValidationSchemas, highestTabReached]
  );

  const handleBack = useCallback(() => {
    if (activeTabIndex > 0) {
      setActiveTabIndex(activeTabIndex - 1);
    }
  }, [activeTabIndex]);

  // Set highest tab reached for edit mode
  useEffect(() => {
    if (id && interviewData) {
      setHighestTabReached(1); // In edit mode, allow access to all tabs
    }
  }, [id, interviewData]);

  useEffect(() => {
    const fetchInterviewData = async () => {
      try {
        const result = await getApi(
          `${API_PATHS.RECRUITMENT_INDIVIDUAL_INTERVIEW}`
        );
        setInterviewData(result?.data?.data || []);
      } catch (e) {
        toast.error(constant.somethingWrong);
      }
    };

    fetchInterviewData();
  }, []);

  const formatJobTitle = (title) => {
    if (!title) return "";
    return title
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
      .replace("Other ", "");
  };

  const handleSubmit = async (values, { resetForm }) => {
    // For creating new feedback, send all required fields
    const feedbackPayload = {
      interviewPanelMembers: values.interviewPanelMembers || [],
      technicalKnowledge: values.technicalKnowledge,
      technicalKnowledgeDetails: values.technicalKnowledgeDetails,
      communication: values.communication,
      problemSolvingSkills: values.problemSolvingSkills,
      problemSolvingDetails: values.problemSolvingDetails,
      rating: values.rating,
      experience: values.experience,
      questionResponses: values.questionResponses,
      recommendedAction: values.recommendedAction,
      comments: values.comments,
    };

    try {
      const response = await postApi(
        `api/recruitment/feedback/${id}`,
        feedbackPayload
      );
      if (response.status === 201) {
        toast.success("Feedback submitted successfully");
        resetForm();
        navigate("/recruitment/interviews");
      } else {
        throw new Error("Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback");
    }
  };

  const fetchCandidates = async () => {
    setIsLoading(true);
    try {
      const result = await getApi("api/recruitment/candidates/?allData=true");
      if (result?.data?.data) {
        setCandidateData(result?.data?.data);
      }
    } catch (e) {
      toast.error(recruitConstant.errorMsg);
      console.error("Error fetching job openings:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  useEffect(() => {
    if (viewMode) {
      const fetchTaskData = async () => {
        try {
          const result = await getApi(API_PATHS.RECRUITMENT_FEEDBACK, id);
          setFeedbackData(result?.data);
        } catch (e) {
          toast.error(constant.somethingWrong);
        }
      };

      fetchTaskData();
    }
  }, [viewMode, id]);

  // Auto-select interview when not in view mode and ID is provided
  useEffect(() => {
    if (!viewMode && id && interviewData && interviewData.length > 0) {
      const matchedInterview = interviewData.find(
        (interview) => interview._id === id
      );
      if (matchedInterview) {
        setSelectedInterview(matchedInterview);
      }
    }
  }, [viewMode, id, interviewData]);

  console.log('Selected Interview:', selectedInterview);

  // Get initial values based on mode
  const getInitialValues = () => {
    if (viewMode && feedbackData) {
      // View mode - populate from feedback data
      // Filter questionResponses based on round
      let filteredResponses = feedbackData.questionResponses || defaultQuestions;
      if (feedbackData.interview?.round === "HR Round") {
        filteredResponses = (feedbackData.questionResponses || []).filter(
          (r) =>
            r.question === "Final Comments" ||
            r.question === feedbackConstants.finalComments
        );
        if (filteredResponses.length === 0) {
          filteredResponses = [
            {
              question: feedbackConstants.finalComments,
              answer: feedbackData.comments || "",
              required: true,
            },
          ];
        }
      }

      return {
        interview: feedbackData.interview?._id || "",
        candidate: feedbackData.candidateName || "",
        candidateEmail: "",
        candidatePhoneNo: "",
        postingTitle: feedbackData.postingTitle || "",
        department: "",
        departmentName: "",
        round: feedbackData.interview?.round || "",
        interviewPanelMembers: feedbackData.interviewPanelMembers || [], // Will be populated user objects
        technicalKnowledge: feedbackData.technicalKnowledge || null,
        technicalKnowledgeDetails: feedbackData.technicalKnowledgeDetails || "",
        communication: feedbackData.communication || null,
        problemSolvingSkills: feedbackData.problemSolvingSkills || null,
        problemSolvingDetails: feedbackData.problemSolvingDetails || "",
        rating: feedbackData.rating,
        experience: feedbackData.experience,
        questionResponses: filteredResponses,
        recommendedAction: feedbackData.recommendedAction || "",
        comments: "",
      };
    } else if (
      !viewMode &&
      id &&
      selectedInterview &&
      Object.keys(selectedInterview).length > 0
    ) {
      // Non-view mode with ID - auto-populate from selected interview
      const candidateId = selectedInterview.application?.candidateDetails?._id;
      const candidate = candidateId && candidateData ? candidateData.find(c => c._id === candidateId) : null;

      return {
        interview: selectedInterview._id || "",
        candidate: selectedInterview.application?.candidateDetails?.name || "",
        candidateEmail:
          selectedInterview.application?.candidateDetails?.email || "",
        candidatePhoneNo:
          selectedInterview.application?.candidateDetails?.phone || "",
        postingTitle: selectedInterview.application?.job?.postingTitle || "",
        department: selectedInterview.department?._id || "",
        departmentName: selectedInterview.department?.name || "",
        round: selectedInterview.round || "",
        interviewPanelMembers: selectedInterview.interviewerIds || [],
        technicalKnowledge: null,
        technicalKnowledgeDetails: "",
        communication: null,
        problemSolvingSkills: null,
        problemSolvingDetails: "",
        rating: null,
        experience: candidate?.experienceRange || null,
        questionResponses: defaultQuestions,
        recommendedAction: "",
        comments: "",
      };
    } else {
      // Default values for new feedback
      return {
        interview: "",
        candidate: "",
        candidateEmail: "",
        candidatePhoneNo: "",
        postingTitle: "",
        department: "",
        departmentName: "",
        round: "",
        interviewPanelMembers: [],
        technicalKnowledge: null,
        technicalKnowledgeDetails: "",
        communication: null,
        problemSolvingSkills: null,
        problemSolvingDetails: "",
        rating: null,
        experience: null,
        questionResponses: defaultQuestions,
        recommendedAction: "",
        comments: "",
      };
    }
  };

  return (
    <>
      <Box>
        <ActionNavbar isCreatePage={true} />
        <Stack>
          <Box className="createJobOpening-container">
            <Box className="createJobOpening-mainContainer">
              <Box className="createJobOpening-breadCrumb">
                <ArrowBackIcon
                  onClick={() => navigate(-1)}
                  fontSize="20px"
                  cursor={"pointer"}
                />
                <Text className="createJobOpening-pageTitle">
                  {viewMode ? feedbackConstants.viewFeedback : feedbackConstants.createFeedback}
                </Text>
              </Box>
            </Box>

            <Formik
              enableReinitialize={true}
              initialValues={getInitialValues()}
              onSubmit={handleSubmit}
            >
              {({
                values,
                setFieldValue,
                handleSubmit,
                errors,
                dirty,
                isValid,
                setErrors,
                setTouched,
              }) => {
                // Calculate tab states for current form state
                const tabStates = getTabStates(
                  values,
                  dirty,
                  isValid,
                  activeTabIndex
                );
                // Check if all mandatory fields are filled
                const allMandatoryFieldsFilled =
                  areMandatoryFieldsFilled(values);

                return (
                  <Form onSubmit={handleSubmit}>
                    <Tabs
                      index={activeTabIndex}
                      onChange={(index) =>
                        handleTabChange(index, values, dirty, isValid)
                      }
                      className="createJobOpening-tabsContainer"
                      variant="unstyled"
                      orientation="vertical"
                    >
                      {/* tab list  */}
                      <TabList
                        w={"40%"}
                        className="createJobOpening-tabListContainer"
                      >
                        {feedbackTabsData.map(({ label, Icon }, index) => {
                          const tabState = tabStates[index];
                          const isCurrentTab = index === activeTabIndex;

                          return (
                            <Tab
                              className="createJobOpening-tabs"
                              key={index}
                              _selected={{
                                color: colors.cta["100"],
                                borderLeft: "2px solid",
                                borderColor: colors.cta["100"],
                              }}
                              _hover={{
                                color: tabState.isAccessible
                                  ? colors.cta["100"]
                                  : "inherit",
                                cursor: tabState.isAccessible
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                              _focus={{ boxShadow: "none" }}
                              display="flex"
                              alignItems="center"
                              gap="8px"
                              opacity={tabState.isAccessible ? 1 : 0.6}
                              cursor={
                                tabState.isAccessible
                                  ? "pointer"
                                  : "not-allowed"
                              }
                              isDisabled={!tabState.isAccessible}
                            >
                              <Icon size={20} />
                              {label}
                              {tabState.isValid && (
                                <Text fontSize="xs" color="green.500">
                                  ✓
                                </Text>
                              )}
                            </Tab>
                          );
                        })}
                      </TabList>

                      {/* tab panels */}
                      <TabPanels
                        w={"60%"}
                        className="createJobOpening-tabsPanelContainer"
                      >
                        {/* Feedback Tab */}
                        <TabPanel className="createJobOpening-tabsPanel">
                          <Box className="createJobOpening-tabsPanelBody">
                            <Flex gap={"20px"}>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="interview">
                                  {feedbackConstants.interview}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="interview">
                                  {({ field, meta, form }) => {
                                    let interviewOptions = [];
                                    let selectedOption = null;

                                    if (viewMode && feedbackData) {
                                      // View mode - show only the feedback's interview
                                      const feedbackInterview =
                                        feedbackData.interview;
                                      if (feedbackInterview) {
                                        interviewOptions = [
                                          {
                                            value: feedbackInterview._id,
                                            label: `${feedbackInterview.interviewId} - ${feedbackInterview.round}`,
                                            id: feedbackInterview._id,
                                          },
                                        ];
                                        selectedOption = interviewOptions[0];
                                      }
                                    } else if (
                                      !viewMode &&
                                      id &&
                                      selectedInterview &&
                                      Object.keys(selectedInterview).length > 0
                                    ) {
                                      // Non-view mode with ID - show selected interview
                                      interviewOptions = [
                                        {
                                          value: selectedInterview._id,
                                          label: `${selectedInterview.interviewId} - ${selectedInterview.round}`,
                                          id: selectedInterview._id,
                                        },
                                      ];
                                      selectedOption = interviewOptions[0];
                                    } else {
                                      // Default - show all interviews
                                      interviewOptions =
                                        interviewData &&
                                          interviewData.length > 0
                                          ? interviewData
                                            .filter((interview) => interview.application && interview.application.job)
                                            .map((interview) => {
                                              const interviewer =
                                                interview.interviewers?.[0]
                                                  ?.panelName;
                                              const formattedTitle =
                                                formatJobTitle(
                                                  interview.application?.job
                                                    ?.postingTitle || "Unknown Job"
                                                );
                                              const interviewerName =
                                                interviewer
                                                  ? `${interviewer.firstName} ${interviewer.lastName}`
                                                  : "Unknown";
                                              const interviewId = interview._id;
                                              return {
                                                value: interviewId,
                                                label: `${formattedTitle} - ${interview.round} - ${interviewerName}`,
                                                id: interviewId,
                                              };
                                            })
                                          : [
                                            {
                                              value: "",
                                              label: "No data found",
                                              isDisabled: true,
                                            },
                                          ];

                                      selectedOption =
                                        interviewOptions.find(
                                          (option) =>
                                            option?.value === field?.value
                                        ) || null;
                                    }

                                    return (
                                      <>
                                        <ReactSelect
                                          isDisabled={
                                            !permission?.create ||
                                            viewMode ||
                                            (!viewMode && id)
                                          }
                                          name={field.name}
                                          value={selectedOption}
                                          onBlur={() =>
                                            form.setFieldTouched(
                                              field.name,
                                              true
                                            )
                                          }
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                          options={interviewOptions}
                                          className="basic-single-select reactSelect"
                                          classNamePrefix="select"
                                          onChange={async (selectedOption) => {
                                            const selectedId = selectedOption
                                              ? selectedOption?.value
                                              : "";
                                            form.setFieldValue(
                                              field.name,
                                              selectedId
                                            );

                                            const selectedInterview =
                                              interviewData.find(
                                                (interview) =>
                                                  interview._id === selectedId
                                              );
                                            setSelectedInterview(
                                              selectedInterview || {}
                                            );

                                            if (selectedId && selectedInterview) {
                                              const applicationDetails =
                                                selectedInterview.application;
                                              if (
                                                applicationDetails?.candidateDetails?.name
                                              ) {
                                                form.setFieldValue(
                                                  "candidate",
                                                  applicationDetails
                                                    .candidateDetails.name
                                                );
                                              }

                                              if (
                                                applicationDetails?.candidateDetails?.email
                                              ) {
                                                form.setFieldValue(
                                                  "candidateEmail",
                                                  applicationDetails
                                                    .candidateDetails.email
                                                );
                                              }

                                              if (
                                                applicationDetails?.candidateDetails?.phone
                                              ) {
                                                form.setFieldValue(
                                                  "candidatePhoneNo",
                                                  applicationDetails
                                                    .candidateDetails.phone
                                                );
                                              }

                                              if (
                                                applicationDetails?.job?.postingTitle
                                              ) {
                                                form.setFieldValue(
                                                  "postingTitle",
                                                  applicationDetails.job
                                                    .postingTitle
                                                );
                                              }

                                              if (
                                                selectedInterview.department
                                              ) {
                                                form.setFieldValue(
                                                  "department",
                                                  selectedInterview.department
                                                    ._id
                                                );
                                                form.setFieldValue(
                                                  "departmentName",
                                                  selectedInterview.department
                                                    .name
                                                );
                                              }

                                              if (selectedInterview.round) {
                                                form.setFieldValue(
                                                  "round",
                                                  selectedInterview.round
                                                );
                                              }

                                              // Auto-fill panel members from selected interview interviewers
                                              form.setFieldValue(
                                                "interviewPanelMembers",
                                                selectedInterview.interviewerIds || []
                                              );

                                              // Auto-fill experience from candidate data
                                              const candidateId = selectedInterview.application?.candidateDetails?._id;
                                              if (candidateId && candidateData) {
                                                const candidate = candidateData.find(c => c._id === candidateId);
                                                if (candidate?.experienceRange) {
                                                  form.setFieldValue(
                                                    "experience",
                                                    candidate.experienceRange
                                                  );
                                                }
                                              }
                                            } else {
                                              form.setFieldValue(
                                                "candidate",
                                                ""
                                              );
                                              form.setFieldValue(
                                                "candidateEmail",
                                                ""
                                              );
                                              form.setFieldValue(
                                                "candidatePhoneNo",
                                                ""
                                              );
                                              form.setFieldValue(
                                                "postingTitle",
                                                ""
                                              );
                                              form.setFieldValue(
                                                "department",
                                                ""
                                              );
                                              form.setFieldValue(
                                                "departmentName",
                                                ""
                                              );
                                              form.setFieldValue("round", "");
                                              form.setFieldValue("interviewPanelMembers", []);
                                            }
                                          }}
                                          placeholder={feedbackConstants.selectInterview}
                                          isClearable={!viewMode && !id}
                                          isSearchable
                                          styles={{
                                            control: (provided) => ({
                                              ...provided,
                                              borderColor:
                                                meta.touched && meta.error
                                                  ? colors.errorText
                                                  : colors.borderColor,
                                            }),
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>
                                            {meta.error}
                                          </Text>
                                        )}
                                      </>
                                    );
                                  }}
                                </Field>
                              </Box>

                              {/* Interview Panel Members */}
                              <Box w={"50%"}>
                                <FormLabel htmlFor="interviewPanelMembers">
                                  {feedbackConstants.interviewPanelMembers}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="interviewPanelMembers">
                                  {({ field, meta, form }) => {
                                    const userOptions = users && users.length > 0
                                      ? users.map((user) => ({
                                        value: user._id,
                                        label: `${user.firstName} ${user.lastName}`,
                                        id: user._id,
                                      }))
                                      : [];

                                    const selectedOptions = field.value && field.value.length > 0
                                      ? field.value.map((member) => {
                                        // Handle both populated objects and IDs
                                        if (typeof member === 'object' && member !== null) {
                                          // Populated user object from API
                                          return {
                                            value: member._id,
                                            label: `${member.firstName} ${member.lastName}`,
                                            id: member._id,
                                          };
                                        } else {
                                          // User ID - find in users list
                                          const foundUser = users?.find((u) => u._id === member);
                                          return foundUser
                                            ? {
                                              value: foundUser._id,
                                              label: `${foundUser.firstName} ${foundUser.lastName}`,
                                              id: foundUser._id,
                                            }
                                            : null;
                                        }
                                      }).filter(Boolean)
                                      : [];

                                    return (
                                      <>
                                        <ReactSelect
                                          isDisabled={true}
                                          isMulti
                                          name={field.name}
                                          value={selectedOptions}
                                          onBlur={() => form.setFieldTouched(field.name, true)}
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                          options={userOptions}
                                          className="basic-multi-select reactSelect"
                                          classNamePrefix="select"
                                          onChange={(selectedOptions) => {
                                            const selectedIds = selectedOptions
                                              ? selectedOptions.map((option) => option.value)
                                              : [];
                                            form.setFieldValue(field.name, selectedIds);
                                          }}
                                          placeholder={feedbackConstants.selectPanelMembers}
                                          styles={{
                                            control: (provided) => ({
                                              ...provided,
                                              borderColor:
                                                meta.touched && meta.error
                                                  ? colors.errorText
                                                  : colors.borderColor,
                                            }),
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>{meta.error}</Text>
                                        )}
                                      </>
                                    );
                                  }}
                                </Field>
                              </Box>

                              {/* Interview Round - Autofilled */}
                              <Box w={"50%"}>
                                <FormLabel htmlFor="round">
                                  {feedbackConstants.interviewRound}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="round">
                                  {({ field, meta, form }) => (
                                    <>
                                      <Input
                                        {...field}
                                        isDisabled={true} // Autofilled from interview
                                        placeholder="Interview round"
                                        borderColor={
                                          meta.touched && meta.error
                                            ? colors.errorText
                                            : colors.borderColor
                                        }
                                      />
                                      {meta.touched && meta.error && (
                                        <Text color={colors.errorText}>{meta.error}</Text>
                                      )}
                                    </>
                                  )}
                                </Field>
                              </Box>
                            </Flex>

                            <Flex gap={"20px"}>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="candidate">
                                  {feedbackConstants.candidateName}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="candidate">
                                  {({ field, meta, form }) => {
                                    let candidateOptions = [];
                                    let selectedOption = null;

                                    if (viewMode && feedbackData) {
                                      // View mode - show candidate from feedback
                                      if (feedbackData.candidateName) {
                                        candidateOptions = [
                                          {
                                            value: feedbackData.candidateName,
                                            label: feedbackData.candidateName,
                                            id: null, // We don't have candidate ID in feedback data
                                          },
                                        ];
                                        selectedOption = candidateOptions[0];
                                      }
                                    } else if (
                                      !viewMode &&
                                      id &&
                                      selectedInterview &&
                                      Object.keys(selectedInterview).length > 0
                                    ) {
                                      // Non-view mode with ID - show candidate from selected interview
                                      const candidateDetails =
                                        selectedInterview.application
                                          ?.candidateDetails;
                                      if (candidateDetails) {
                                        candidateOptions = [
                                          {
                                            value: candidateDetails._id,
                                            label: candidateDetails.name,
                                            id: candidateDetails._id,
                                          },
                                        ];
                                        selectedOption = candidateOptions[0];
                                      }
                                    } else {
                                      // Default - show all candidates
                                      candidateOptions =
                                        candidateData &&
                                          candidateData.length > 0
                                          ? candidateData.map((option) => ({
                                            value: option._id,
                                            label: option.name,
                                            id: option._id,
                                          }))
                                          : [
                                            {
                                              value: "",
                                              label: "No data found",
                                              isDisabled: true,
                                            },
                                          ];

                                      selectedOption =
                                        candidateOptions.find(
                                          (option) =>
                                            option?.value === field?.value
                                        ) || null;
                                    }

                                    return (
                                      <>
                                        <ReactSelect
                                          isDisabled={
                                            !permission?.create ||
                                            viewMode ||
                                            (!viewMode && id)
                                          }
                                          name={field.name}
                                          value={selectedOption}
                                          onBlur={() =>
                                            form.setFieldTouched(
                                              field.name,
                                              true
                                            )
                                          }
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                          options={candidateOptions}
                                          className="basic-single-select reactSelect"
                                          classNamePrefix="select"
                                          onChange={(selectedOption) => {
                                            const selectedValue = selectedOption
                                              ? selectedOption.value
                                              : "";
                                            const selectedId = selectedOption
                                              ? selectedOption.id
                                              : null;

                                            form.setFieldValue(
                                              field.name,
                                              selectedValue
                                            );

                                            const selectedCandidate =
                                              candidateData?.find(
                                                (candidate) =>
                                                  candidate._id === selectedId
                                              );

                                            setSelectedCandidate(
                                              selectedCandidate || {}
                                            );

                                            if (selectedCandidate) {
                                              form.setFieldValue(
                                                "candidateEmail",
                                                selectedCandidate.email || ""
                                              );
                                              form.setFieldValue(
                                                "candidatePhoneNo",
                                                selectedCandidate.phone || ""
                                              );
                                            } else {
                                              form.setFieldValue(
                                                "candidateEmail",
                                                ""
                                              );
                                              form.setFieldValue(
                                                "candidatePhoneNo",
                                                ""
                                              );
                                            }
                                          }}
                                          placeholder={feedbackConstants.selectCandidateName}
                                          isClearable={!viewMode && !id}
                                          isSearchable
                                          styles={{
                                            control: (provided) => ({
                                              ...provided,
                                              borderColor:
                                                meta.touched && meta.error
                                                  ? colors.errorText
                                                  : colors.borderColor,
                                            }),
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>
                                            {meta.error}
                                          </Text>
                                        )}
                                      </>
                                    );
                                  }}
                                </Field>
                              </Box>
                            </Flex>

                            <Flex gap={"20px"}>
                              <Box w={"50%"}>
                                <FormLabel htmlFor="postingTitle">
                                  {feedbackConstants.profilePosition}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="postingTitle">
                                  {({ field, meta, form }) => {
                                    let selectedOption = null;

                                    if (viewMode || (!viewMode && id)) {
                                      // In view mode or non-view mode with ID, show the posting title as text
                                      const postingTitle = field.value;
                                      if (postingTitle) {
                                        selectedOption = {
                                          value: postingTitle,
                                          label: formatJobTitle(postingTitle),
                                        };
                                      }
                                    } else {
                                      selectedOption =
                                        jobTitleOptions.find(
                                          (option) =>
                                            option.value === field.value
                                        ) || null;
                                    }

                                    return (
                                      <>
                                        <ReactSelect
                                          isDisabled={
                                            !permission?.create ||
                                            viewMode ||
                                            (!viewMode && id)
                                          }
                                          name={field.name}
                                          value={selectedOption}
                                          onBlur={() =>
                                            form.setFieldTouched(
                                              field.name,
                                              true
                                            )
                                          }
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                          options={jobTitleOptions}
                                          className="basic-single-select reactSelect"
                                          classNamePrefix="select"
                                          onChange={(selectedOption) => {
                                            const selectedValue = selectedOption
                                              ? selectedOption.value
                                              : "";
                                            form.setFieldValue(
                                              field.name,
                                              selectedValue
                                            );
                                          }}
                                          placeholder="Select Job Title"
                                          isClearable={!viewMode && !id}
                                          isSearchable
                                          styles={{
                                            control: (provided) => ({
                                              ...provided,
                                              borderColor:
                                                meta.touched && meta.error
                                                  ? colors.errorText
                                                  : colors.borderColor,
                                            }),
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>
                                            {meta.error}
                                          </Text>
                                        )}
                                      </>
                                    );
                                  }}
                                </Field>
                              </Box>

                              <Box w={"50%"}>
                                <FormLabel htmlFor="rating">
                                  {feedbackConstants.finalRating}
                                  <span className="astrick">*</span>
                                </FormLabel>

                                <Field name="rating">
                                  {({ field, meta, form }) => (
                                    <>
                                      <Box
                                        sx={{
                                          "& svg": {
                                            fontSize: "2.5rem",
                                            marginRight: "10px",
                                          },
                                          "& span": {
                                            fontSize: "2.5rem",
                                            marginRight: "10px",
                                          },
                                          cursor:
                                            !canEdit
                                              ? "default"
                                              : "pointer",
                                        }}
                                      >
                                        <Rating
                                          value={form.values.rating}
                                          readOnly={
                                            !canEdit
                                          }
                                          onChange={(newValue) => {
                                            if (
                                              !viewMode &&
                                              permission?.create
                                            ) {
                                              form.setFieldValue(
                                                "rating",
                                                newValue
                                              );
                                            }
                                          }}
                                        />
                                      </Box>

                                      {meta.touched && meta.error && (
                                        <Text color={colors.errorText}>
                                          {meta.error}
                                        </Text>
                                      )}
                                    </>
                                  )}
                                </Field>
                              </Box>
                            </Flex>

                            {/* Experience and Recommended Action */}
                            <Flex gap={"20px"}>
                              <Box w={"50%"}>
                                <FormLabel
                                  display="flex"
                                  ms="4px"
                                  fontSize="sm"
                                  fontWeight="500"
                                  mb="2px"
                                >
                                  {feedbackConstants.experienceInYears} <span className="astrick">*</span>
                                </FormLabel>

                                <Field name="experience">
                                  {({ field, form, meta }) => (
                                    <>
                                      <Select
                                        fontSize="sm"
                                        fontWeight="500"
                                        name={field.name}
                                        value={field.value || ""}
                                        placeholder={feedbackConstants.selectExperienceRange}
                                        onChange={(e) =>
                                          form.setFieldValue(
                                            field.name,
                                            e.target.value
                                          )
                                        }
                                        onBlur={() =>
                                          form.setFieldTouched(field.name, true)
                                        }
                                        isDisabled={
                                          true
                                        }
                                        borderColor={
                                          meta.touched && meta.error
                                            ? "red.300"
                                            : null
                                        }
                                      >
                                        <option value="" disabled hidden>
                                          {feedbackConstants.selectExperienceRange}
                                        </option>
                                        {experienceRangeOptions.map((option) => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </Select>

                                      <Text mb="10px" color="red">
                                        {meta.touched && meta.error && meta.error}
                                      </Text>
                                    </>
                                  )}
                                </Field>
                              </Box>

                              <Box w={"50%"}>
                                <FormLabel htmlFor="recommendedAction">
                                  {feedbackConstants.recommendedAction}
                                  <span className="astrick">*</span>
                                </FormLabel>
                                <Field name="recommendedAction">
                                  {({ field, meta, form }) => {
                                    const selectedAction =
                                      recommendedActions.find(
                                        (option) => option.value === field.value
                                      ) || null;

                                    return (
                                      <>
                                        <ReactSelect
                                          isDisabled={
                                            !canEdit
                                          }
                                          name={field.name}
                                          value={selectedAction}
                                          onBlur={() =>
                                            form.setFieldTouched(field.name, true)
                                          }
                                          menuPortalTarget={document.body}
                                          menuPosition="fixed"
                                          options={recommendedActions}
                                          className="basic-single-select reactSelect"
                                          classNamePrefix="select"
                                          onChange={(selectedOption) => {
                                            const selectedValue = selectedOption
                                              ? selectedOption.value
                                              : "";
                                            form.setFieldValue(
                                              field.name,
                                              selectedValue
                                            );
                                          }}
                                          placeholder={feedbackConstants.selectRecommendedAction}
                                          isClearable={!viewMode}
                                          styles={{
                                            control: (provided) => ({
                                              ...provided,
                                              borderColor:
                                                meta.touched && meta.error
                                                  ? colors.errorText
                                                  : colors.borderColor,
                                            }),
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>
                                            {meta.error}
                                          </Text>
                                        )}
                                      </>
                                    );
                                  }}
                                </Field>
                              </Box>
                            </Flex>

                            {/* Technical Ratings (Hidden for HR Round) */}
                            {values.round !== "HR Round" && (
                              <>
                                <Flex gap={"20px"}>
                                  <Box w={"50%"}>
                                    <FormLabel htmlFor="technicalKnowledge">
                                      {feedbackConstants.technicalKnowledge}
                                      {!viewMode && (
                                        <span className="astrick">*</span>
                                      )}
                                    </FormLabel>
                                    <Field name="technicalKnowledge">
                                      {({ field, meta, form }) => (
                                        <>
                                          <Box
                                            sx={{
                                              "& svg": {
                                                fontSize: "2.5rem",
                                                marginRight: "10px",
                                              },
                                              "& span": {
                                                fontSize: "2.5rem",
                                                marginRight: "10px",
                                              },
                                              cursor: !canEdit
                                                ? "default"
                                                : "pointer",
                                            }}
                                          >
                                            <Rating
                                              value={
                                                form.values.technicalKnowledge ||
                                                0
                                              }
                                              readOnly={!canEdit}
                                              onChange={(newValue) => {
                                                if (
                                                  !viewMode &&
                                                  permission?.create
                                                ) {
                                                  form.setFieldValue(
                                                    "technicalKnowledge",
                                                    newValue
                                                  );
                                                }
                                              }}
                                            />
                                          </Box>
                                          {meta.touched && meta.error && (
                                            <Text color={colors.errorText}>
                                              {meta.error}
                                            </Text>
                                          )}
                                        </>
                                      )}
                                    </Field>
                                  </Box>

                                  <Box w={"50%"}>
                                    <FormLabel htmlFor="communication">
                                      {feedbackConstants.communication}
                                      {!viewMode && (
                                        <span className="astrick">*</span>
                                      )}
                                    </FormLabel>
                                    <Field name="communication">
                                      {({ field, meta, form }) => (
                                        <>
                                          <Box
                                            sx={{
                                              "& svg": {
                                                fontSize: "2.5rem",
                                                marginRight: "10px",
                                              },
                                              "& span": {
                                                fontSize: "2.5rem",
                                                marginRight: "10px",
                                              },
                                              cursor: !canEdit
                                                ? "default"
                                                : "pointer",
                                            }}
                                          >
                                            <Rating
                                              value={
                                                form.values.communication || 0
                                              }
                                              readOnly={!canEdit}
                                              onChange={(newValue) => {
                                                if (
                                                  !viewMode &&
                                                  permission?.create
                                                ) {
                                                  if (newValue >= 3) {
                                                    form.setFieldValue(
                                                      "communication",
                                                      newValue
                                                    );
                                                  }
                                                }
                                              }}
                                            />
                                          </Box>
                                          {meta.touched && meta.error && (
                                            <Text color={colors.errorText}>
                                              {meta.error}
                                            </Text>
                                          )}
                                        </>
                                      )}
                                    </Field>
                                  </Box>
                                </Flex>

                                <Box w={"100%"}>
                                  <Text className="textEditorHeading">
                                    {feedbackConstants.technicalKnowledgeDetails}
                                    {!viewMode && <span className="astrick">*</span>}
                                  </Text>
                                  <Field name="technicalKnowledgeDetails">
                                    {({ field, meta }) => (
                                      <>
                                        <TextEditor
                                          isDisabled={!canEdit}
                                          toolbarId="technical-knowledge-details"
                                          value={field.value || ""}
                                          handleChange={(value) => {
                                            setFieldValue(
                                              "technicalKnowledgeDetails",
                                              value
                                            );
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>
                                            {meta.error}
                                          </Text>
                                        )}
                                      </>
                                    )}
                                  </Field>
                                </Box>

                                <Flex gap={"20px"}>
                                  <Box w={"50%"}>
                                    <FormLabel htmlFor="problemSolvingSkills">
                                      {feedbackConstants.problemSolvingSkills}
                                      {!viewMode && (
                                        <span className="astrick">*</span>
                                      )}
                                    </FormLabel>
                                    <Field name="problemSolvingSkills">
                                      {({ field, meta, form }) => (
                                        <>
                                          <Box
                                            sx={{
                                              "& svg": {
                                                fontSize: "2.5rem",
                                                marginRight: "10px",
                                              },
                                              "& span": {
                                                fontSize: "2.5rem",
                                                marginRight: "10px",
                                              },
                                              cursor: !canEdit
                                                ? "default"
                                                : "pointer",
                                            }}
                                          >
                                            <Rating
                                              value={
                                                form.values
                                                  .problemSolvingSkills || 0
                                              }
                                              readOnly={!canEdit}
                                              onChange={(newValue) => {
                                                if (
                                                  !viewMode &&
                                                  permission?.create
                                                ) {
                                                  form.setFieldValue(
                                                    "problemSolvingSkills",
                                                    newValue
                                                  );
                                                }
                                              }}
                                            />
                                          </Box>
                                          {meta.touched && meta.error && (
                                            <Text color={colors.errorText}>
                                              {meta.error}
                                            </Text>
                                          )}
                                        </>
                                      )}
                                    </Field>
                                  </Box>
                                </Flex>

                                <Box w={"100%"}>
                                  <Text className="textEditorHeading">
                                    {feedbackConstants.problemSolvingDetails}
                                    {!viewMode && <span className="astrick">*</span>}
                                  </Text>
                                  <Field name="problemSolvingDetails">
                                    {({ field, meta }) => (
                                      <>
                                        <TextEditor
                                          isDisabled={!canEdit}
                                          toolbarId="problem-solving-details"
                                          value={field.value || ""}
                                          handleChange={(value) => {
                                            setFieldValue(
                                              "problemSolvingDetails",
                                              value
                                            );
                                          }}
                                        />
                                        {meta.touched && meta.error && (
                                          <Text color={colors.errorText}>
                                            {meta.error}
                                          </Text>
                                        )}
                                      </>
                                    )}
                                  </Field>
                                </Box>
                              </>
                            )}

                            {/* Question Responses */}
                            <Box w={"100%"}>
                              {values.questionResponses.map(
                                (questionResponse, index) => {
                                  // For HR Round, skip everything except Final Comments
                                  const isFinalComments =
                                    questionResponse.question ===
                                    "Final Comments" ||
                                    questionResponse.question ===
                                    feedbackConstants.finalComments;

                                  if (
                                    values.round === "HR Round" &&
                                    !isFinalComments
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <Box key={index} mb={4}>
                                      <Text className="textEditorHeading" mb={2}>
                                        {questionResponse.question}
                                        {questionResponse.required && (
                                          <span className="astrick">*</span>
                                        )}
                                      </Text>
                                      <Field
                                        name={`questionResponses.${index}.answer`}
                                      >
                                        {({ field, meta }) => (
                                          <>
                                            <TextEditor
                                              isDisabled={!canEdit}
                                              toolbarId={`question-${index}`}
                                              value={field.value || ""}
                                              handleChange={(content) => {
                                                const cleanedValue =
                                                  content === "<p><br></p>"
                                                    ? ""
                                                    : content;
                                                setFieldValue(
                                                  `questionResponses.${index}.answer`,
                                                  cleanedValue
                                                );
                                              }}
                                              handleBlur={() => { }}
                                              borderColor={
                                                meta.touched && meta.error
                                                  ? colors.errorText
                                                  : colors.borderColor
                                              }
                                              errorMsg={
                                                meta.touched && meta.error
                                                  ? meta.error
                                                  : ""
                                              }
                                            />
                                            {meta.touched && meta.error && (
                                              <Text color={colors.errorText}>
                                                {meta.error}
                                              </Text>
                                            )}
                                          </>
                                        )}
                                      </Field>
                                    </Box>
                                  );
                                }
                              )}
                            </Box>

                            {/* Remove old comments section */}
                          </Box>

                          <Box className="createJobOpening-tabsPanelFooter">
                            <Button
                              className="theme-btn2 cancelBtn"
                              sx={{
                                textTransform: "capitalize",
                              }}
                              variant="outline"
                              colorScheme={colors.error["100"]}
                              size="sm"
                              onClick={() => navigate(-1)}
                            >
                              {constant.back}
                            </Button>
                            {!viewMode && (
                              <Button
                                disabled={
                                  isLoading ||
                                  (!id && !dirty) ||
                                  !isValid ||
                                  !permission?.create ||
                                  !allMandatoryFieldsFilled
                                }
                                className="nextBtn"
                                variant="primary"
                                type="submit"
                              >
                                {isLoading ? (
                                  <Spinner size="sm" />
                                ) : (
                                  constant.save
                                )}
                              </Button>
                            )}
                          </Box>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default CreateFeedback;
